import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ReactLenis } from "@studio-freight/react-lenis";
import { Toaster } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import ErrorBoundary from "@/components/ErrorBoundary";

// Public Pages
import Home from "@/pages/Home";
import Portfolio from "@/pages/Portfolio";
import ProjectDetail from "@/pages/ProjectDetail";
import Services from "@/pages/Services";
import Booking from "@/pages/Booking";
import Reviews from "@/pages/Reviews";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Privacy from "@/pages/Privacy";
import GiftVoucher from "@/pages/GiftVoucher";
import GiftVoucherSuccess from "@/pages/GiftVoucherSuccess";
import AdminVouchers from "@/pages/admin/Vouchers";
import TrackBooking from "@/pages/TrackBooking";
import PaymentSuccess from "@/pages/PaymentSuccess";
import ManageBooking from "@/pages/ManageBooking";
import NotFound from "@/pages/NotFound";

// Admin Pages
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProjects from "@/pages/admin/Projects";
import AdminBookings from "@/pages/admin/Bookings";
import AdminReviews from "@/pages/admin/Reviews";
import AdminServices from "@/pages/admin/Services";
import AdminSettings from "@/pages/admin/Settings";
import AdminAvailability from "@/pages/admin/Availability";
import AdminLeads from "@/pages/admin/Leads";
import AdminAccount from "@/pages/admin/Account";
import AdminPayments from "@/pages/admin/Payments";
import AdminEmailLogs from "@/pages/admin/EmailLogs";
import AdminBackup from "@/pages/admin/Backup";

// Layouts
import PublicLayout from "@/layouts/PublicLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Auth Provider
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(reg => console.log('SW registered:', reg.scope))
      .catch(err => console.log('SW registration failed:', err));
  });
}

function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ReactLenis root options={{ lerp: 0.1, duration: 1.2, smoothWheel: true }}>
          <BrowserRouter>
            <ScrollToTop />
            <PWAInstallPrompt />
            <Routes>
              {/* Public Routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/portfolio/:slug" element={<ProjectDetail />} />
                <Route path="/servizi" element={<Services />} />
                <Route path="/prenota" element={<Booking />} />
                <Route path="/buono-regalo" element={<GiftVoucher />} />
                <Route path="/buono-regalo/grazie" element={<GiftVoucherSuccess />} />
                <Route path="/recensioni" element={<Reviews />} />
                <Route path="/chi-sono" element={<About />} />
                <Route path="/contatti" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/traccia-prenotazione" element={<TrackBooking />} />
                <Route path="/gestisci-prenotazione" element={<ManageBooking />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancelled" element={<Booking />} />
              </Route>

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="progetti" element={<AdminProjects />} />
                <Route path="prenotazioni" element={<AdminBookings />} />
                <Route path="pagamenti" element={<AdminPayments />} />
                <Route path="recensioni" element={<AdminReviews />} />
                <Route path="servizi" element={<AdminServices />} />
                <Route path="disponibilita" element={<AdminAvailability />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="email-logs" element={<AdminEmailLogs />} />
                <Route path="backup" element={<AdminBackup />} />
                <Route path="buoni-regalo" element={<AdminVouchers />} />
                <Route path="impostazioni" element={<AdminSettings />} />
                <Route path="account" element={<AdminAccount />} />
              </Route>

              {/* 404 - Catch all unmatched routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster position="bottom-right" richColors />
          </BrowserRouter>
        </ReactLenis>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
